import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { tokenService } from "../services/tokenService";
import { getApiUrl } from "../config/api";

type OrgUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  role: "user" | "owner" | "admin" | "superadmin";
  createdAt: string;
};

const OrgUsers: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<
    "email" | "name" | "createdAt" | "status"
  >("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isOwner = user?.role === "owner";

  const fetchUsers = useMemo(
    () => async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await tokenService.makeAuthenticatedRequest(
          getApiUrl("/api/owner/users")
        );
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        setUsers(data);
      } catch (e: any) {
        setError(e?.message || "Ошибка загрузки пользователей");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isOwner) {
      fetchUsers();
    }
  }, [isOwner, fetchUsers]);

  const [form, setForm] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showOwnerPwdForUserId, setShowOwnerPwdForUserId] = useState<
    string | null
  >(null);
  const [ownerPwdInput, setOwnerPwdInput] = useState("");
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await tokenService.makeAuthenticatedRequest(
        getApiUrl("/api/owner/users"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: form.email,
            password: form.password,
            firstName: form.firstName || undefined,
            lastName: form.lastName || undefined,
          }),
        }
      );
      if (!res.ok) throw new Error("Не удалось создать пользователя");
      await fetchUsers();
      setForm({ email: "", password: "", firstName: "", lastName: "" });
    } catch (e: any) {
      setSubmitError(e?.message || "Ошибка");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (u: OrgUser) => {
    try {
      const action = u.isActive ? "заблокировать" : "разблокировать";
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `Вы уверены, что хотите ${action} пользователя ${u.email}?`
      );
      if (!ok) return;
      const res = await tokenService.makeAuthenticatedRequest(
        getApiUrl(`/api/owner/users/${u.id}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: !u.isActive }),
        }
      );
      if (res.ok) {
        setUsers((prev) =>
          prev.map((x) => (x.id === u.id ? { ...x, isActive: !x.isActive } : x))
        );
      }
    } catch {}
  };

  const requestRevealPassword = (userId: string) => {
    setShowOwnerPwdForUserId(userId);
    setOwnerPwdInput("");
    setRevealedPassword(null);
  };

  const confirmRevealPassword = async () => {
    if (!showOwnerPwdForUserId) return;
    try {
      const res = await tokenService.makeAuthenticatedRequest(
        getApiUrl(`/api/owner/users/${showOwnerPwdForUserId}/reset-password`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ownerPassword: ownerPwdInput }),
        }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Не удалось показать пароль");
      }
      const data = await res.json();
      setRevealedPassword(data?.newPassword || "");
    } catch (e: any) {
      alert(e?.message || "Ошибка");
    }
  };

  // Derived data and helpers must be declared before any early return
  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? users
      : users.filter((u) => {
          const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
          return u.email.toLowerCase().includes(q) || name.includes(q);
        });

    const sorted = [...filtered].sort((a, b) => {
      let comp = 0;
      if (sortKey === "email") {
        comp = a.email.localeCompare(b.email);
      } else if (sortKey === "name") {
        const an = `${a.firstName || ""} ${a.lastName || ""}`.trim();
        const bn = `${b.firstName || ""} ${b.lastName || ""}`.trim();
        comp = an.localeCompare(bn);
      } else if (sortKey === "createdAt") {
        comp =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortKey === "status") {
        comp = Number(a.isActive) - Number(b.isActive);
      }
      return sortDir === "asc" ? comp : -comp;
    });

    return sorted;
  }, [users, search, sortKey, sortDir]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visibleUsers.slice(start, start + pageSize);
  }, [visibleUsers, page, pageSize]);

  const formatDate = (iso?: string) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso as string;
    }
  };

  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold">Доступ запрещён</h1>
        <p className="text-sm text-gray-600 mt-2">
          Только владелец может управлять пользователями.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Управление пользователями
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Создавайте пользователей своей организации, управляйте их доступом
              и статусом.
            </p>
          </div>
          <div className="w-full md:w-[28rem]">
            <div className="relative">
              <input
                type="text"
                placeholder="Поиск по email или имени..."
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <form
          className="card p-5 space-y-5"
          onSubmit={onCreate}
          autoComplete="off"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 bg-white">
              <div className="text-sm font-medium text-gray-900 mb-2.5">
                Личные данные
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Имя
                </label>
                <input
                  type="text"
                  placeholder="Имя"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  required
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Фамилия
                </label>
                <input
                  type="text"
                  placeholder="Фамилия (необязательно)"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, lastName: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 bg-white">
              <div className="text-sm font-medium text-gray-900 mb-2.5">
                Данные аккаунта
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="user@company.com"
                  required
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={form.email}
                  name="new_email"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
                <div className="text-xs text-gray-500">
                  Рабочий email пользователя
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Пароль
                </label>
                <input
                  type="password"
                  placeholder="Минимум 6 символов"
                  required
                  minLength={6}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={form.password}
                  name="new_password"
                  autoComplete="new-password"
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                />
                <div className="text-xs text-gray-500">
                  Рекомендуйте надёжный пароль
                </div>
              </div>
            </div>
          </div>
          {submitError && (
            <div className="text-sm text-red-600">{submitError}</div>
          )}
          <div className="flex justify-end">
            <button className="btn btn-primary" disabled={submitting}>
              {submitting ? "Создание..." : "Добавить пользователя"}
            </button>
          </div>
        </form>

        <div className="card p-0 overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b gap-3 flex-wrap">
            <div className="text-lg font-semibold">
              Пользователи ({visibleUsers.length})
            </div>
            <div className="flex items-center gap-2 text-sm">
              <label className="text-gray-600">Сортировать:</label>
              <select
                className="border rounded px-2 py-1"
                value={`${sortKey}:${sortDir}`}
                onChange={(e) => {
                  const [k, d] = e.target.value.split(":");
                  setSortKey(k as any);
                  setSortDir(d as any);
                  setPage(1);
                }}
              >
                <option value="createdAt:desc">По дате (новые сверху)</option>
                <option value="createdAt:asc">По дате (старые сверху)</option>
                <option value="email:asc">Email (А→Я)</option>
                <option value="email:desc">Email (Я→А)</option>
                <option value="name:asc">Имя (А→Я)</option>
                <option value="name:desc">Имя (Я→А)</option>
                <option value="status:asc">Статус (Заблок. сначала)</option>
                <option value="status:desc">Статус (Актив. сначала)</option>
              </select>
              <label className="text-gray-600 ml-3">На странице:</label>
              <select
                className="border rounded px-2 py-1"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            {loading && (
              <div className="text-sm text-gray-500">Загрузка...</div>
            )}
            {error && <div className="text-sm text-red-600">{error}</div>}
          </div>
          {!loading && !error && users.length === 0 ? (
            <div className="px-5 py-8 text-sm text-gray-500">
              Пока нет пользователей
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Пользователь
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Статус
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Создан
                    </th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedUsers.map((u) => {
                    const initial = (u.firstName || u.email || "U")
                      .charAt(0)
                      .toUpperCase();
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">
                              {initial}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {u.email}
                              </div>
                              <div className="text-xs text-gray-600">
                                {(u.firstName || "").trim()}{" "}
                                {(u.lastName || "").trim()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                          >
                            {u.isActive ? "Активен" : "Заблокирован"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {formatDate(u.createdAt)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            className={`px-3 py-1.5 text-sm rounded-md border transition ${u.isActive ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                            onClick={() => toggleActive(u)}
                          >
                            {u.isActive ? "Заблокировать" : "Разблокировать"}
                          </button>
                          <button
                            className="ml-2 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                            onClick={() => requestRevealPassword(u.id)}
                            title="Показать пароль"
                          >
                            Показать пароль
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-5 py-4 flex items-center justify-between border-t text-sm">
            <div className="text-gray-600">
              Показано{" "}
              {Math.min((page - 1) * pageSize + 1, visibleUsers.length)}–
              {Math.min(page * pageSize, visibleUsers.length)} из{" "}
              {visibleUsers.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1.5 rounded border disabled:opacity-50"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Назад
              </button>
              <span className="text-gray-600">Стр. {page}</span>
              <button
                className="px-3 py-1.5 rounded border disabled:opacity-50"
                disabled={page * pageSize >= visibleUsers.length}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд
              </button>
            </div>
          </div>
        </div>
      </div>
      {showOwnerPwdForUserId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <form
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-5"
            autoComplete="off"
          >
            <div className="text-lg font-semibold">Подтверждение личности</div>
            <div className="text-sm text-gray-600 mt-1">
              Введите пароль владельца, чтобы показать пароль пользователя.
            </div>
            {/* Dummy fields to deflect browser autofill */}
            <input
              type="text"
              name="fake_username"
              autoComplete="username"
              className="absolute -top-[9999px] left-0 h-0 w-0 opacity-0 pointer-events-none"
            />
            <input
              type="password"
              name="fake_password"
              autoComplete="new-password"
              className="absolute -top-[9999px] left-0 h-0 w-0 opacity-0 pointer-events-none"
            />

            <div className="mt-4 space-y-2">
              <label className="block text-sm text-gray-700">
                Пароль владельца
              </label>
              <input
                type="password"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                name="owner_password_confirm"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                value={ownerPwdInput}
                onChange={(e) => setOwnerPwdInput(e.target.value)}
                autoFocus
              />
              {revealedPassword != null && (
                <div className="mt-3 p-3 bg-gray-50 rounded border text-sm">
                  <div className="text-gray-600">Пароль пользователя:</div>
                  <div className="font-mono text-gray-900 break-all">
                    {revealedPassword || "—"}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded border"
                onClick={() => {
                  setShowOwnerPwdForUserId(null);
                  setOwnerPwdInput("");
                  setRevealedPassword(null);
                }}
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  confirmRevealPassword();
                }}
              >
                Подтвердить
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default OrgUsers;
